import React, { useState, useEffect } from 'react';
import AIPaperGenerator from './AIPaperGenerator';
import PaperSheet from './AIPaper/PaperSheet';
import { SHEET_W } from './AIPaper/sheetStyles';
import { downloadSheetPdf, printSheet, safeFileName } from './AIPaper/exportUtils';
import { listPapers, getPaper, logDownload, errMsg } from './AIPaper/aiPaperApi';
import TeacherPaperStats from './AIPaper/TeacherPaperStats';

const EXAMS = ['Unit Test', 'Half-Yearly', 'Annual', 'Practice'];
const LANGS = [['english', 'English'], ['hindi', 'हिंदी'], ['bilingual', 'Bilingual']];

// Compact generation panel. "Generate Paper" opens the existing wizard pre-filled with these choices.
function TeacherPaperHub({ user, cls, academicYear }) {
  const [subject, setSubject] = useState(user.subject || '');
  const [klass, setKlass] = useState(cls || '');
  const [examType, setExamType] = useState('Unit Test');
  const [maxMarks, setMaxMarks] = useState(20);
  const [languageMode, setLanguageMode] = useState('english');
  const [open, setOpen] = useState(false);
  const [papers, setPapers] = useState([]);
  const [msg, setMsg] = useState('');
  const [job, setJob] = useState(null); // { paper, action }
  const [statsKey, setStatsKey] = useState(0);

  const load = () => {
    setStatsKey((k) => k + 1);
    return listPapers(user).then((p) => setPapers(p.slice(0, 5))).catch(() => setPapers([]));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const startExport = async (id, action) => {
    setMsg('');
    try {
      setJob({ paper: await getPaper(user, id), action });
    } catch (e) {
      setMsg(errMsg(e));
    }
  };

  // Hidden full-size sheet is mounted first, then exported.
  useEffect(() => {
    if (!job) return undefined;
    const t = setTimeout(async () => {
      const el = document.getElementById('th-export-sheet');
      const { paper, action } = job;
      try {
        if (action === 'pdf') await downloadSheetPdf(el, `${safeFileName(paper.subject)}_Class${safeFileName(paper.class)}_Question_Paper.pdf`);
        else await printSheet(el, `${paper.subject} Question Paper`);
        await logDownload(user, paper._id, 'question');
        setStatsKey((k) => k + 1);
      } catch (e) {
        setMsg(errMsg(e));
      }
      setJob(null);
    }, 300);
    return () => clearTimeout(t);
  }, [job]);

  const canGenerate = subject.trim() && klass.trim();

  return (
    <section className="th-card">
      <div className="th-card-head"><h2>AI Question Paper</h2></div>
      <div className="th-grid2">
        <input className="th-input" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" />
        <input className="th-input" value={klass} onChange={(e) => setKlass(e.target.value)} placeholder="Class" />
        <select className="th-input" value={examType} onChange={(e) => setExamType(e.target.value)}>
          {EXAMS.map((x) => <option key={x}>{x}</option>)}
        </select>
        <input className="th-input" type="number" min="1" value={maxMarks} onChange={(e) => setMaxMarks(e.target.value)} placeholder="Marks" />
      </div>
      <div className="th-seg">
        {LANGS.map(([v, l]) => (
          <button key={v} type="button" className={languageMode === v ? 'on' : ''} onClick={() => setLanguageMode(v)}>{l}</button>
        ))}
      </div>
      <button className="th-btn th-btn-block" disabled={!canGenerate} onClick={() => setOpen(true)}>Generate Paper</button>
      <p className="th-muted th-hint">You’ll upload the chapter PDF in the next step; questions come only from it.</p>

      <TeacherPaperStats user={user} reloadKey={statsKey} />

      <h3 className="th-sub-h">Recent Papers</h3>
      {msg && <div className="th-error">{msg}</div>}
      {papers.length === 0 ? (
        <p className="th-empty">No papers yet.</p>
      ) : (
        <ul className="th-list">
          {papers.map((p) => (
            <li key={p._id} className="th-paper">
              <div className="th-row-title">{p.title}</div>
              <div className="th-muted">Class {p.class} · {p.subject} · {new Date(p.updatedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</div>
              <div className="th-actions">
                <button className="th-btn th-btn-sm" disabled={!!job} onClick={() => startExport(p._id, 'pdf')}>Download PDF</button>
                <button className="th-btn th-btn-sm th-btn-outline" disabled={!!job} onClick={() => startExport(p._id, 'print')}>Print</button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {job && (
        <div style={{ position: 'absolute', left: -99999, top: 0, width: SHEET_W }} aria-hidden="true">
          <PaperSheet id="th-export-sheet" paper={job.paper} mode="question" logoSrc="/logo.png" />
        </div>
      )}

      {open && (
        <AIPaperGenerator
          user={user}
          academicYear={academicYear}
          initialConfig={{ subject: subject.trim(), class: klass.trim(), examType, maxMarks: Number(maxMarks) || 20, languageMode }}
          onClose={() => { setOpen(false); load(); }}
        />
      )}
    </section>
  );
}

export default TeacherPaperHub;
